import React, { useContext, useRef, useState } from 'react';
import { AlertType, FloatingAlert } from './FloatingAlert';

interface SnackStack {
    snacks: Snack[];
    setSnacks: (update: (bars: Snack[]) => Snack[]) => void;
}

interface Snack {
    id: number;
    message: string;
    style: AlertType;
    autohideDuration: number;
}

const context = React.createContext<SnackStack>({
    snacks: [],
    setSnacks: () => {
        throw new Error('missing snackbar context');
    },
});

export function SnackbarProvider({ children }: { children: React.ReactNode }) {
    const [snacks, setSnacks] = useState<Snack[]>([]);

    return (
        <context.Provider value={{ snacks, setSnacks }}>
            {snacks.map((snack) => {
                function dismiss() {
                    setSnacks((snacks) => snacks.filter((s) => s.id !== snack.id));
                }

                return (
                    <FloatingAlert
                        isOpen={true}
                        alertType={snack.style}
                        onClose={dismiss}
                        autoHideDuration={
                            snack.autohideDuration === 0 ? undefined : snack.autohideDuration
                        }
                        key={snack.id}
                        message={snack.message}
                    />
                );
            })}
            {children}
        </context.Provider>
    );
}

export type snackbarFn = (message: string, autohideDuration: number) => void;

export function useSnackbar() {
    const stack = useContext(context);
    const lastId = useRef(0);

    function pushMessage(snack: Omit<Snack, 'id'>) {
        lastId.current++;

        stack.setSnacks((snacks) => [...snacks, { ...snack, id: lastId.current }]);
    }

    return {
        error: (message: string, autohideDuration = 5000) =>
            pushMessage({ message, style: 'error', autohideDuration }),
        info: (message: string, autohideDuration = 5000) =>
            pushMessage({ message, style: 'info', autohideDuration }),
        success: (message: string, autohideDuration = 5000) =>
            pushMessage({ message, style: 'success', autohideDuration }),
        saveError: () =>
            pushMessage({
                message: __('Unable to save, please try again later.'),
                style: 'error',
                autohideDuration: 5000,
            }),
    };
}
